import type {
  ChatMessage,
  GpuCapability,
  InferenceMessage,
  RemoteLedgerOrigin,
  ResponseMode,
  SwarmScope,
  SwarmNode,
  SwarmSession,
} from "../types";
import {
  NEXUS_ACTIVATION_PACKET_FORMAT,
  NEXUS_LAYER_BYTE_PACKET_FORMAT,
  NEXUS_LAYER_STREAM_TECH_ID,
  type LayerBytePacket,
  type LayerStreamManifest,
  type SerializedActivationPacket,
} from "../experimental/layer-stream";
import {
  decryptJson,
  deriveSwarmCredentials,
  encryptJson,
  generatePasscode,
  generateSwarmCode,
  isValidPasscode,
  KDF_ITERATIONS,
  normalizeSwarmCode,
  randomSalt,
  type CipherEnvelope,
} from "./crypto";
import { createId } from "./ids";
import { flushLedgerOutbox, postLedgerOrQueue } from "./ledger-outbox";

interface ApiErrorBody {
  error?: string | { code?: string; message?: string };
  message?: string;
}

class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

interface AuthResponse {
  nodeId: string;
  socketPath: string;
}

interface ChallengeResponse {
  salt: string;
  iterations: number;
}

interface IceConfigResponse {
  iceServers: RTCIceServer[];
  turnConfigured: boolean;
}

interface LedgerEntryResponse {
  seq: number;
  messageId: string;
  eventType: string;
  ciphertext: string;
  iv: string;
  createdByNodeId?: string | null;
  createdByDeviceName?: string | null;
  createdAt: number;
}

interface LedgerPageResponse {
  entries: LedgerEntryResponse[];
  nextAfter: number;
}

export interface SwarmGenerationPayload {
  messages: InferenceMessage[];
  mode: ResponseMode;
  modelId: string;
  temperature: number;
  maxNewTokens: number;
  contextTokens: 2048 | 4096 | 8192;
}

export interface RemoteInferenceTask {
  taskId: string;
  batchId: string;
  role: string;
  fromPeerId: string;
  payload: SwarmGenerationPayload;
}

export interface DistributedInferenceResult {
  taskId: string;
  batchId: string;
  role: string;
  fromPeerId: string;
  deviceName: string;
  text: string;
  tokens?: number;
  tokensPerSecond?: number;
}

export interface RemoteInferenceCancel {
  taskId: string;
  batchId: string;
  fromPeerId: string;
}

export type SwarmProgressState = "queued" | "running" | "complete" | "error" | "cancelled";

export interface SwarmProgress {
  fromPeerId: string;
  deviceName: string;
  taskId: string;
  batchId: string;
  state: SwarmProgressState;
}

export interface RemoteLayerStreamManifest {
  fromPeerId: string;
  manifest: LayerStreamManifest;
}

export interface RemoteLayerBytePacket {
  fromPeerId: string;
  packet: LayerBytePacket;
}

export interface RemoteActivationPacket {
  fromPeerId: string;
  packet: SerializedActivationPacket;
}

export interface LayerPacketAck {
  packetKind: "layer-bytes" | "activation-packet";
  streamId: string;
  sequence: number;
  totalSequences: number;
}

export interface ConnectedPeer extends SwarmNode {
  connectionState: RTCPeerConnectionState;
  dataChannelState: RTCDataChannelState | "none";
  rttMs?: number;
}

interface PeerRecord {
  info: SwarmNode;
  connection: RTCPeerConnection;
  channel: RTCDataChannel | null;
  pendingCandidates: PendingIceCandidate[];
  pendingCandidateBytes: number;
  pendingCandidateExpiryTimer: ReturnType<typeof globalThis.setTimeout> | null;
  receiveQueue: Promise<void>;
  pendingDataMessages: number;
  receiveWindowStartedAt: number;
  receivedCharactersInWindow: number;
  rttMs?: number;
}

interface PendingIceCandidate {
  candidate: RTCIceCandidateInit;
  byteSize: number;
  queuedAt: number;
}

interface RemoteTaskCharge {
  acceptedAt: number;
  requestedTokens: number;
}

interface RemoteTaskBudget {
  charges: RemoteTaskCharge[];
  cooldownUntil: number;
  lastSeenAt: number;
}

type SignalData =
  | { kind: "offer" | "answer"; sdp: string }
  | {
      kind: "ice";
      candidate: string;
      sdpMid?: string | null;
      sdpMLineIndex?: number | null;
      usernameFragment?: string | null;
    }
  | { kind: "ice-complete" };

type SignalingMessage =
  | { type: "welcome"; selfId: string; peers: SwarmNode[] }
  | { type: "peer-joined"; peer: SwarmNode }
  | { type: "peer-left"; nodeId: string }
  | { type: "presence"; peer: SwarmNode }
  | { type: "signal"; from: string; data: SignalData }
  | {
      type: "progress";
      from: string;
      deviceName: string;
      taskId: string;
      batchId: string;
      state: SwarmProgressState;
    }
  | { type: "pong"; nonce: string; sentAt: number };

type PeerMessage =
  | {
      kind: "task";
      taskId: string;
      batchId: string;
      role: string;
      payload: SwarmGenerationPayload;
    }
  | {
      kind: "result";
      taskId: string;
      batchId: string;
      role: string;
      deviceName: string;
      text: string;
      tokens?: number;
      tokensPerSecond?: number;
    }
  | { kind: "cancel"; taskId: string; batchId: string }
  | { kind: "layer-manifest"; manifest: LayerStreamManifest }
  | { kind: "layer-bytes"; packet: LayerBytePacket }
  | { kind: "activation-packet"; packet: SerializedActivationPacket }
  | { kind: "layer-ack"; ack: LayerPacketAck }
  | { kind: "ping"; nonce: string; sentAt: number }
  | { kind: "pong"; nonce: string; sentAt: number };

interface ChunkFrame {
  v: 1;
  type: "chunk";
  messageId: string;
  index: number;
  total: number;
  data: string;
}

interface ChunkAssembly {
  chunks: string[];
  received: number;
  receivedCharacters: number;
  createdAt: number;
}

interface PendingBatch {
  expectedPeerByTask: Map<string, string>;
  results: Map<string, DistributedInferenceResult>;
  resolve: (results: DistributedInferenceResult[]) => void;
  reject: (error: Error) => void;
  timeout: number;
  abortSignal?: AbortSignal;
  abortHandler?: () => void;
}

interface PendingLayerAck {
  resolve: () => void;
  reject: (error: Error) => void;
  timeout: number;
}

const FALLBACK_ICE_SERVERS: RTCIceServer[] = [
  { urls: ["stun:stun.cloudflare.com:3478"] },
];
const CHUNK_CHARACTERS = 12_000;
const MAX_CHUNKS = 128;
const MAX_ASSEMBLIES = 16;
const MAX_ASSEMBLIES_PER_PEER = 4;
const MAX_BUFFERED_ASSEMBLY_CHARACTERS = 2_000_000;
const DATA_RECEIVE_WINDOW_MS = 10_000;
const MAX_RECEIVED_CHARACTERS_PER_WINDOW = 2_000_000;
const MAX_QUEUED_DATA_MESSAGES_PER_PEER = 64;
const MAX_SIGNAL_BYTES = 32_000;
const MAX_LEDGER_CIPHERTEXT_CHARS = 330_000;
const MAX_PENDING_ICE_CANDIDATES_PER_PEER = 64;
const MAX_PENDING_ICE_BYTES_PER_PEER = 128 * 1024;
const MAX_PENDING_ICE_BYTES_TOTAL = 512 * 1024;
const MAX_PENDING_ICE_AGE_MS = 30_000;
const MAX_REMOTE_TASK_PROMPT_CHARACTERS = 64_000;
const REMOTE_TASK_WINDOW_MS = 60_000;
const MAX_REMOTE_TASKS_PER_WINDOW = 8;
const MAX_REMOTE_REQUESTED_TOKENS_PER_WINDOW = 8_192;
const REMOTE_TASK_COOLDOWN_MS = 30_000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function jsonLength(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).byteLength;
}

async function parseApiResponse<T>(response: Response): Promise<T> {
  const body = (await response.json().catch(() => ({}))) as T & ApiErrorBody;
  if (!response.ok) {
    const nestedError = isRecord(body.error) ? body.error : undefined;
    throw new ApiRequestError(
      body.message
        || (typeof body.error === "string" ? body.error : undefined)
        || (typeof nestedError?.message === "string" ? nestedError.message : undefined)
        || `通信に失敗しました (${response.status})`,
      response.status,
      typeof nestedError?.code === "string" ? nestedError.code : undefined,
    );
  }
  return body;
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 15_000);
  try {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (init.body) headers.set("Content-Type", "application/json");
    const response = await fetch(path, {
      ...init,
      headers,
      credentials: "same-origin",
      signal: controller.signal,
    });
    return await parseApiResponse<T>(response);
  } finally {
    window.clearTimeout(timeout);
  }
}

function capabilitiesFromGpu(
  gpu: GpuCapability,
  acceptTasks = true,
  scope: SwarmScope = "friend",
): Record<string, unknown> {
  return {
    webgpu: gpu.available,
    adapterName: gpu.adapterName,
    architecture: gpu.architecture,
    shaderF16: gpu.shaderF16,
    maxBufferSize: gpu.maxBufferSize,
    acceptTasks,
    scope,
    worldDistributed: scope === "world",
    modelHost: true,
  };
}

function websocketUrl(path: string): string {
  const url = new URL(path, window.location.href);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

function isSignalData(value: unknown): value is SignalData {
  if (!isRecord(value)) return false;
  if (
    (value.kind === "offer" || value.kind === "answer")
    && typeof value.sdp === "string"
    && value.sdp.length > 0
    && value.sdp.length <= 24_000
  ) {
    return true;
  }
  if (value.kind === "ice-complete") return true;
  return (
    value.kind === "ice"
    && typeof value.candidate === "string"
    && value.candidate.length > 0
    && value.candidate.length <= 4096
  );
}

function isSignalingMessage(value: unknown): value is SignalingMessage {
  return isRecord(value) && typeof value.type === "string";
}

function isProgressMessage(value: Record<string, unknown>): value is Extract<SignalingMessage, { type: "progress" }> {
  return (
    value.type === "progress"
    && typeof value.from === "string"
    && /^[0-9a-f-]{36}$/u.test(value.from)
    && typeof value.deviceName === "string"
    && value.deviceName.length > 0
    && value.deviceName.length <= 64
    && typeof value.taskId === "string"
    && /^[A-Za-z0-9_-]{1,64}$/u.test(value.taskId)
    && typeof value.batchId === "string"
    && /^[A-Za-z0-9_-]{1,64}$/u.test(value.batchId)
    && typeof value.state === "string"
    && ["queued", "running", "complete", "error", "cancelled"].includes(value.state)
  );
}

function isChunkFrame(value: unknown): value is ChunkFrame {
  if (!isRecord(value)) return false;
  return (
    value.v === 1 &&
    value.type === "chunk" &&
    typeof value.messageId === "string" &&
    value.messageId.length > 0 &&
    value.messageId.length <= 128 &&
    typeof value.index === "number" &&
    Number.isInteger(value.index) &&
    typeof value.total === "number" &&
    Number.isInteger(value.total) &&
    typeof value.data === "string" &&
    value.total > 0 &&
    value.total <= MAX_CHUNKS &&
    value.index >= 0 &&
    value.index < value.total &&
    value.data.length <= CHUNK_CHARACTERS
  );
}

function isSafeIdentifier(value: unknown, maxLength = 128): value is string {
  return typeof value === "string" && /^[A-Za-z0-9_.:-]+$/u.test(value) && value.length > 0 && value.length <= maxLength;
}

function isLayerStreamManifest(value: unknown): value is LayerStreamManifest {
  if (!isRecord(value) || value.technologyId !== NEXUS_LAYER_STREAM_TECH_ID) return false;
  if (!isSafeIdentifier(value.modelId) || value.fullModelDistributed !== false) return false;
  const totalLayers = typeof value.totalLayers === "number" ? value.totalLayers : Number.NaN;
  if (!Number.isInteger(totalLayers) || totalLayers <= 0 || totalLayers > 4096) return false;
  if (!Array.isArray(value.chunks) || value.chunks.length < 1 || value.chunks.length > 256) return false;
  let cursor = 0;
  for (const chunk of value.chunks) {
    if (!isRecord(chunk)) return false;
    const startLayer = typeof chunk.startLayer === "number" ? chunk.startLayer : Number.NaN;
    const endLayerExclusive = typeof chunk.endLayerExclusive === "number" ? chunk.endLayerExclusive : Number.NaN;
    const layerCount = typeof chunk.layerCount === "number" ? chunk.layerCount : Number.NaN;
    if (!isSafeIdentifier(chunk.chunkId, 256) || !isSafeIdentifier(chunk.deviceId)) return false;
    if (
      !Number.isInteger(startLayer)
      || !Number.isInteger(endLayerExclusive)
      || startLayer !== cursor
      || endLayerExclusive <= startLayer
      || endLayerExclusive > totalLayers
      || layerCount !== endLayerExclusive - startLayer
      || chunk.delivery !== "on-demand-encrypted-stream"
    ) return false;
    if (chunk.dependsOnChunkId !== null && !isSafeIdentifier(chunk.dependsOnChunkId, 256)) return false;
    cursor = endLayerExclusive;
  }
  return cursor === totalLayers
    && value.modelDistribution === "layer-range-only"
    && value.activationTransport === NEXUS_ACTIVATION_PACKET_FORMAT;
}

function isBase64(value: unknown, maxLength: number): value is string {
  return typeof value === "string"
    && value.length <= maxLength
    && value.length % 4 === 0
    && /^[A-Za-z0-9+/]*={0,2}$/u.test(value);
}

function isLayerBytePacket(value: unknown): value is LayerBytePacket {
  const sequence = isRecord(value) && typeof value.sequence === "number" ? value.sequence : Number.NaN;
  const totalSequences = isRecord(value) && typeof value.totalSequences === "number" ? value.totalSequences : Number.NaN;
  const byteOffset = isRecord(value) && typeof value.byteOffset === "number" ? value.byteOffset : Number.NaN;
  const totalBytes = isRecord(value) && typeof value.totalBytes === "number" ? value.totalBytes : Number.NaN;
  return (
    isRecord(value)
    && value.format === NEXUS_LAYER_BYTE_PACKET_FORMAT
    && isSafeIdentifier(value.streamId)
    && isSafeIdentifier(value.modelId)
    && typeof value.chunkId === "string"
    && value.chunkId.length > 0
    && value.chunkId.length <= 256
    && Number.isInteger(sequence)
    && Number.isInteger(totalSequences)
    && Number.isInteger(byteOffset)
    && Number.isInteger(totalBytes)
    && sequence >= 0
    && totalSequences > 0
    && totalSequences <= MAX_CHUNKS
    && sequence < totalSequences
    && byteOffset >= 0
    && totalBytes >= 0
    && totalBytes <= 67_108_864
    && isBase64(value.dataBase64, CHUNK_CHARACTERS * MAX_CHUNKS)
  );
}

function isSerializedActivationPacket(value: unknown): value is SerializedActivationPacket {
  if (!isRecord(value) || value.format !== NEXUS_ACTIVATION_PACKET_FORMAT) return false;
  const encoded = value.encoded;
  const tokenIndex = typeof value.tokenIndex === "number" ? value.tokenIndex : Number.NaN;
  const layerStart = typeof value.layerStart === "number" ? value.layerStart : Number.NaN;
  const layerEndExclusive = typeof value.layerEndExclusive === "number" ? value.layerEndExclusive : Number.NaN;
  const sequence = typeof value.sequence === "number" ? value.sequence : Number.NaN;
  const totalSequences = typeof value.totalSequences === "number" ? value.totalSequences : Number.NaN;
  const originalBytes = typeof value.originalBytes === "number" ? value.originalBytes : Number.NaN;
  const encodedBytes = typeof value.encodedBytes === "number" ? value.encodedBytes : Number.NaN;
  const elementCount = isRecord(encoded) && typeof encoded.elementCount === "number" ? encoded.elementCount : Number.NaN;
  const blockSize = isRecord(encoded) && typeof encoded.blockSize === "number" ? encoded.blockSize : Number.NaN;
  return (
    value.codec === "activation-delta-int8-rle-v1"
    && isSafeIdentifier(value.streamId)
    && Number.isInteger(tokenIndex)
    && tokenIndex >= 0
    && Number.isInteger(layerStart)
    && Number.isInteger(layerEndExclusive)
    && layerEndExclusive > layerStart
    && Number.isInteger(sequence)
    && Number.isInteger(totalSequences)
    && sequence >= 0
    && totalSequences > 0
    && totalSequences <= MAX_CHUNKS
    && sequence < totalSequences
    && Number.isInteger(originalBytes)
    && Number.isInteger(encodedBytes)
    && originalBytes >= 0
    && encodedBytes >= 0
    && isRecord(encoded)
    && encoded.format === "activation-delta-int8-rle-v1"
    && (encoded.mode === "absolute" || encoded.mode === "delta")
    && Number.isInteger(elementCount)
    && elementCount >= 0
    && elementCount <= 16_777_216
    && Number.isInteger(blockSize)
    && blockSize > 0
    && isBase64(encoded.scalesBase64, CHUNK_CHARACTERS * MAX_CHUNKS)
    && isBase64(encoded.payloadBase64, CHUNK_CHARACTERS * MAX_CHUNKS)
  );
}

function isLayerPacketAck(value: unknown): value is LayerPacketAck {
  if (!isRecord(value)) return false;
  const sequence = typeof value.sequence === "number" ? value.sequence : Number.NaN;
  const totalSequences = typeof value.totalSequences === "number" ? value.totalSequences : Number.NaN;
  return (
    (value.packetKind === "layer-bytes" || value.packetKind === "activation-packet")
    && isSafeIdentifier(value.streamId)
    && Number.isInteger(sequence)
    && Number.isInteger(totalSequences)
    && sequence >= 0
    && totalSequences > 0
    && totalSequences <= MAX_CHUNKS
    && sequence < totalSequences
  );
}

function isPeerMessage(value: unknown): value is PeerMessage {
  if (!isRecord(value) || typeof value.kind !== "string") return false;
  if (value.kind === "ping" || value.kind === "pong") {
    return (
      typeof value.nonce === "string"
      && value.nonce.length > 0
      && value.nonce.length <= 128
      && typeof value.sentAt === "number"
      && Number.isFinite(value.sentAt)
    );
  }
  if (value.kind === "task") {
    const payload = value.payload;
    if (!isRecord(payload) || !Array.isArray(payload.messages) || payload.messages.length > 64) {
      return false;
    }
    const messagesAreSafe = payload.messages.every(
      (message) => isRecord(message)
        && ["system", "user", "assistant"].includes(String(message.role))
        && typeof message.content === "string"
        && message.content.length <= MAX_REMOTE_TASK_PROMPT_CHARACTERS,
    );
    const totalMessageCharacters = payload.messages.reduce(
      (total, message) => total + (isRecord(message) && typeof message.content === "string" ? message.content.length : 0),
      0,
    );
    return (
      typeof value.taskId === "string"
      && value.taskId.length <= 128
      && typeof value.batchId === "string"
      && value.batchId.length <= 128
      && ["primary", "critic", "verifier", "alternative"].includes(String(value.role))
      && messagesAreSafe
      && totalMessageCharacters <= MAX_REMOTE_TASK_PROMPT_CHARACTERS
      && typeof payload.modelId === "string"
      && payload.modelId.length <= 256
      && typeof payload.temperature === "number"
      && Number.isFinite(payload.temperature)
      && payload.temperature >= 0
      && payload.temperature <= 2
      && typeof payload.maxNewTokens === "number"
      && Number.isInteger(payload.maxNewTokens)
      && payload.maxNewTokens >= 1
      && payload.maxNewTokens <= 2048
      && typeof payload.contextTokens === "number"
      && [2048, 4096, 8192].includes(payload.contextTokens)
      && ["fast", "medium", "thinking"].includes(String(payload.mode))
    );
  }
  if (value.kind === "cancel") {
    return (
      typeof value.taskId === "string"
      && value.taskId.length > 0
      && value.taskId.length <= 128
      && typeof value.batchId === "string"
      && value.batchId.length > 0
      && value.batchId.length <= 128
    );
  }
  if (value.kind === "layer-manifest") return isLayerStreamManifest(value.manifest);
  if (value.kind === "layer-bytes") return isLayerBytePacket(value.packet);
  if (value.kind === "activation-packet") return isSerializedActivationPacket(value.packet);
  if (value.kind === "layer-ack") return isLayerPacketAck(value.ack);
  return (
    value.kind === "result"
    && typeof value.taskId === "string"
    && value.taskId.length <= 128
    && typeof value.batchId === "string"
    && value.batchId.length <= 128
    && ["primary", "critic", "verifier", "alternative"].includes(String(value.role))
    && typeof value.deviceName === "string"
    && value.deviceName.length <= 80
    && typeof value.text === "string"
    && value.text.length <= 100_000
    && (value.tokens === undefined
      || (typeof value.tokens === "number" && Number.isInteger(value.tokens) && value.tokens >= 0))
    && (value.tokensPerSecond === undefined
      || (typeof value.tokensPerSecond === "number" && Number.isFinite(value.tokensPerSecond)))
  );
}

function isSwarmNode(value: unknown): value is SwarmNode {
  return (
    isRecord(value)
    && typeof value.id === "string"
    && typeof value.name === "string"
    && typeof value.modelId === "string"
    && typeof value.lastSeen === "number"
    && isRecord(value.capabilities)
  );
}

function isLedgerEntry(value: unknown): value is LedgerEntryResponse {
  return (
    isRecord(value)
    && typeof value.seq === "number"
    && Number.isSafeInteger(value.seq)
    && value.seq > 0
    && typeof value.messageId === "string"
    && /^[A-Za-z0-9_-]{1,64}$/u.test(value.messageId)
    && typeof value.eventType === "string"
    && /^[a-z][a-z0-9.-]{0,63}$/u.test(value.eventType)
    && typeof value.ciphertext === "string"
    && value.ciphertext.length > 0
    && value.ciphertext.length <= 350_000
    && typeof value.iv === "string"
    && value.iv.length === 16
    && (value.createdByNodeId === undefined
      || value.createdByNodeId === null
      || (typeof value.createdByNodeId === "string"
        && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u.test(value.createdByNodeId)))
    && (value.createdByDeviceName === undefined
      || value.createdByDeviceName === null
      || (typeof value.createdByDeviceName === "string" && value.createdByDeviceName.length <= 64))
    && typeof value.createdAt === "number"
    && Number.isSafeInteger(value.createdAt)
  );
}

function remoteLedgerOrigin(entry: LedgerEntryResponse): RemoteLedgerOrigin | undefined {
  if (
    typeof entry.createdByNodeId !== "string"
    || !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u.test(entry.createdByNodeId)
    || typeof entry.createdByDeviceName !== "string"
  ) return undefined;

  const deviceName = entry.createdByDeviceName
    .replace(/[\p{Cc}\p{Cf}]/gu, "")
    .trim()
    .slice(0, 64);
  if (!deviceName) return undefined;
  return { kind: "remote-ledger", nodeId: entry.createdByNodeId, deviceName };
}

function parseSyncedChatTurn(value: unknown): ChatMessage[] {
  if (!isRecord(value) || value.v !== 1 || !isRecord(value.user) || !isRecord(value.assistant)) return [];
  const user = value.user;
  const assistant = value.assistant;
  if (
    typeof user.id !== "string"
    || !/^[A-Za-z0-9_-]{1,64}$/u.test(user.id)
    || typeof user.content !== "string"
    || user.content.length > 80_000
    || typeof user.createdAt !== "number"
    || !Number.isSafeInteger(user.createdAt)
    || typeof assistant.id !== "string"
    || !/^[A-Za-z0-9_-]{1,64}$/u.test(assistant.id)
    || typeof assistant.content !== "string"
    || assistant.content.length > 100_000
    || typeof assistant.createdAt !== "number"
    || !Number.isSafeInteger(assistant.createdAt)
    || (assistant.source !== "local" && assistant.source !== "swarm")
  ) {
    return [];
  }
  return [
    {
      id: user.id,
      role: "user",
      content: user.content,
      createdAt: user.createdAt,
      state: "complete",
    },
    {
      id: assistant.id,
      role: "assistant",
      content: assistant.content,
      createdAt: assistant.createdAt,
      state: "complete",
      metrics: { source: assistant.source },
    },
  ];
}

export class SwarmClient extends EventTarget {
  private session: SwarmSession | null = null;
  private socket: WebSocket | null = null;
  private iceServers: RTCIceServer[] = FALLBACK_ICE_SERVERS;
  private readonly peers = new Map<string, PeerRecord>();
  private readonly assemblies = new Map<string, ChunkAssembly>();
  private readonly pendingBatches = new Map<string, PendingBatch>();
  private readonly pendingLayerAcks = new Map<string, PendingLayerAck>();
  private readonly seenRemoteTasks = new Set<string>();
  private readonly remoteTaskBudgets = new Map<string, RemoteTaskBudget>();
  private presenceTimer: number | null = null;
  private pingTimer: number | null = null;
  private signalingRttMs: number | null = null;
  private currentModelId = "";
  private currentGpu: GpuCapability | null = null;
  private turnConfigured = false;
  private acceptTasks = true;
  private scope: SwarmScope = "friend";
  private intentionalClose = false;

  get current(): SwarmSession | null {
    return this.session;
  }

  get signalingLatency(): number | null {
    return this.signalingRttMs;
  }

  get hasTurn(): boolean {
    return this.turnConfigured;
  }

  listPeers(): ConnectedPeer[] {
    return [...this.peers.values()].map((peer) => ({
      ...peer.info,
      connectionState: peer.connection.connectionState,
      dataChannelState: peer.channel?.readyState ?? "none",
      rttMs: peer.rttMs,
    }));
  }

  private emit<T>(name: string, detail: T): void {
    this.dispatchEvent(new CustomEvent<T>(name, { detail }));
  }

  private async loadIceConfig(): Promise<void> {
    try {
      const session = this.requireSession();
      const config = await api<IceConfigResponse>(`/api/ice?code=${encodeURIComponent(session.code)}`);
      this.iceServers = config.iceServers.length ? config.iceServers : FALLBACK_ICE_SERVERS;
      this.turnConfigured = config.turnConfigured;
    } catch {
      this.iceServers = FALLBACK_ICE_SERVERS;
      this.turnConfigured = false;
    }
  }

  async create(options: {
    deviceName: string;
    modelId: string;
    gpu: GpuCapability;
    acceptTasks?: boolean;
    scope?: SwarmScope;
  }): Promise<SwarmSession> {
    let lastError: Error | null = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const code = generateSwarmCode();
      const passcode = generatePasscode();
      const salt = randomSalt();
      const derived = await deriveSwarmCredentials(code, passcode, salt, KDF_ITERATIONS);
      try {
        this.acceptTasks = options.acceptTasks ?? false;
        this.scope = options.scope ?? "friend";
        const result = await api<AuthResponse>("/api/swarms", {
          method: "POST",
          body: JSON.stringify({
            code,
            salt,
            iterations: KDF_ITERATIONS,
            verifier: derived.verifier,
            deviceName: options.deviceName,
            modelId: options.modelId,
            capabilities: capabilitiesFromGpu(options.gpu, this.acceptTasks, this.scope),
          }),
        });
        this.session = {
          code,
          passcode,
          nodeId: result.nodeId,
          socketPath: result.socketPath,
          deviceName: options.deviceName,
          encryptionKey: derived.encryptionKey,
          created: true,
        };
        try {
          await this.connect(options.modelId, options.gpu);
        } catch (error) {
          await this.rollbackSession(true);
          throw error;
        }
        return this.session;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        if (!(error instanceof ApiRequestError) || error.status !== 409) break;
      }
    }
    throw lastError ?? new Error("分散グループを作成できませんでした。");
  }

  async join(options: {
    code: string;
    passcode: string;
    deviceName: string;
    modelId: string;
    gpu: GpuCapability;
    acceptTasks?: boolean;
    scope?: SwarmScope;
  }): Promise<SwarmSession> {
    const code = normalizeSwarmCode(options.code);
    if (!/^[A-Z0-9]{8}$/.test(code)) throw new Error("分散コードは大文字英数字8文字です。");
    if (!isValidPasscode(options.passcode)) {
      throw new Error("PassCodeは英字・数字・記号を含む8文字です。");
    }
    const challenge = await api<ChallengeResponse>(`/api/swarms/${code}/challenge`);
    const derived = await deriveSwarmCredentials(
      code,
      options.passcode,
      challenge.salt,
      challenge.iterations,
    );
    this.acceptTasks = options.acceptTasks ?? false;
    this.scope = options.scope ?? "friend";
    const result = await api<AuthResponse>(`/api/swarms/${code}/join`, {
      method: "POST",
      body: JSON.stringify({
        verifier: derived.verifier,
        deviceName: options.deviceName,
        modelId: options.modelId,
        capabilities: capabilitiesFromGpu(options.gpu, this.acceptTasks, this.scope),
      }),
    });
    this.session = {
      code,
      passcode: options.passcode,
      nodeId: result.nodeId,
      socketPath: result.socketPath,
      deviceName: options.deviceName,
      encryptionKey: derived.encryptionKey,
      created: false,
    };
    try {
      await this.connect(options.modelId, options.gpu);
    } catch (error) {
      await this.rollbackSession(false);
      throw error;
    }
    return this.session;
  }

  private async connect(modelId: string, gpu: GpuCapability): Promise<void> {
    const session = this.requireSession();
    this.currentModelId = modelId;
    this.currentGpu = gpu;
    this.intentionalClose = false;
    await this.loadIceConfig();

    await new Promise<void>((resolve, reject) => {
      const socket = new WebSocket(websocketUrl(session.socketPath));
      this.socket = socket;
      const timeout = window.setTimeout(() => {
        socket.close();
        reject(new Error("リアルタイム接続がタイムアウトしました。"));
      }, 12_000);
      let welcomed = false;

      socket.addEventListener("open", () => {
        this.sendPresence();
      });
      socket.addEventListener("message", (event) => {
        void this.handleSignalMessage(event.data).then((messageType) => {
          if (messageType === "welcome" && !welcomed) {
            welcomed = true;
            window.clearTimeout(timeout);
            resolve();
          }
        }).catch(() => {
          if (!welcomed) {
            window.clearTimeout(timeout);
            reject(new Error("シグナリングメッセージを処理できませんでした。"));
          }
          socket.close(1008, "Invalid signaling response");
        });
      });
      socket.addEventListener("error", () => {
        if (!welcomed) {
          window.clearTimeout(timeout);
          reject(new Error("Durable ObjectのWebSocketに接続できませんでした。"));
        }
        this.emit("status", { status: "error", message: "シグナリング接続でエラーが発生しました。" });
      });
      socket.addEventListener("close", () => {
        this.stopTimers();
        if (!this.intentionalClose) {
          this.emit("status", { status: "disconnected", message: "シグナリング接続が切れました。" });
        }
      });
    });

    this.startTimers();
    void flushLedgerOutbox().catch(() => undefined);
    this.emit("status", { status: "connected", message: "分散グループに接続しました。" });
  }

  private startTimers(): void {
    this.stopTimers();
    this.presenceTimer = window.setInterval(() => this.sendPresence(), 15_000);
    this.pingTimer = window.setInterval(() => {
      const sentAt = Date.now();
      const nonce = createId("ping");
      this.sendSignal({ type: "ping", nonce, sentAt });
      for (const peerId of this.peers.keys()) {
        void this.sendPeerMessage(peerId, { kind: "ping", nonce, sentAt }).catch(() => undefined);
      }
    }, 10_000);
  }

  private stopTimers(): void {
    if (this.presenceTimer !== null) window.clearInterval(this.presenceTimer);
    if (this.pingTimer !== null) window.clearInterval(this.pingTimer);
    this.presenceTimer = null;
    this.pingTimer = null;
  }

  updatePresence(
    modelId: string,
    gpu: GpuCapability,
    acceptTasks = this.acceptTasks,
    scope: SwarmScope = this.scope,
  ): void {
    this.currentModelId = modelId;
    this.currentGpu = gpu;
    this.acceptTasks = acceptTasks;
    this.scope = scope;
    this.sendPresence();
  }

  private sendPresence(): void {
    if (!this.currentGpu) return;
    this.sendSignal({
      type: "presence",
      modelId: this.currentModelId,
      capabilities: capabilitiesFromGpu(this.currentGpu, this.acceptTasks, this.scope),
    });
  }

  private sendSignal(message: Record<string, unknown>): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    if (jsonLength(message) > MAX_SIGNAL_BYTES) throw new Error("シグナリングメッセージが大きすぎます。");
    this.socket.send(JSON.stringify(message));
  }

  private async handleSignalMessage(raw: unknown): Promise<string | null> {
    if (typeof raw !== "string" || raw.length > MAX_SIGNAL_BYTES) return null;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
    if (!isSignalingMessage(parsed)) return null;

    if (parsed.type === "welcome") {
      if (!Array.isArray(parsed.peers)) return null;
      for (const peer of parsed.peers.filter(isSwarmNode)) {
        if (peer.id !== this.session?.nodeId) await this.ensurePeer(peer, true);
      }
      this.emit("peers", this.listPeers());
      return parsed.type;
    }
    if (parsed.type === "peer-joined") {
      if (!isRecord(parsed.peer) || typeof parsed.peer.id !== "string") return null;
      this.emit("peers", this.listPeers());
      return parsed.type;
    }
    if (parsed.type === "peer-left") {
      this.removePeer(parsed.nodeId);
      return parsed.type;
    }
    if (parsed.type === "presence") {
      if (!isSwarmNode(parsed.peer)) return null;
      const peer = this.peers.get(parsed.peer.id);
      if (peer) peer.info = parsed.peer;
      this.emit("peers", this.listPeers());
      return parsed.type;
    }
    if (parsed.type === "signal") {
      if (!isSignalData(parsed.data)) return null;
      await this.handlePeerSignal(parsed.from, parsed.data);
      return parsed.type;
    }
    if (parsed.type === "progress") {
      if (!isProgressMessage(parsed)) return null;
      this.emit<SwarmProgress>("progress", {
        fromPeerId: parsed.from,
        deviceName: parsed.deviceName,
        taskId: parsed.taskId,
        batchId: parsed.batchId,
        state: parsed.state,
      });
      return parsed.type;
    }
    if (parsed.type === "pong") {
      this.signalingRttMs = Math.max(0, Date.now() - parsed.sentAt);
      this.emit("latency", {
        signalingRttMs: this.signalingRttMs,
        peers: this.listPeers(),
      });
      return parsed.type;
    }
    return null;
  }

  private async ensurePeer(info: SwarmNode, initiator: boolean): Promise<PeerRecord> {
    const existing = this.peers.get(info.id);
    if (existing) {
      existing.info = info;
      return existing;
    }
    const connection = new RTCPeerConnection({ iceServers: this.iceServers });
    const peer: PeerRecord = {
      info,
      connection,
      channel: null,
      pendingCandidates: [],
      pendingCandidateBytes: 0,
      pendingCandidateExpiryTimer: null,
      receiveQueue: Promise.resolve(),
      pendingDataMessages: 0,
      receiveWindowStartedAt: Date.now(),
      receivedCharactersInWindow: 0,
    };
    this.peers.set(info.id, peer);

    connection.addEventListener("icecandidate", (event) => {
      if (event.candidate) {
        const candidate = event.candidate.toJSON();
        this.sendSignal({
          type: "signal",
          to: info.id,
          data: {
            kind: "ice",
            candidate: candidate.candidate,
            sdpMid: candidate.sdpMid,
            sdpMLineIndex: candidate.sdpMLineIndex,
            usernameFragment: candidate.usernameFragment,
          },
        });
      } else {
        this.sendSignal({ type: "signal", to: info.id, data: { kind: "ice-complete" } });
      }
    });
    connection.addEventListener("connectionstatechange", () => {
      if (["failed", "closed"].includes(connection.connectionState)) this.removePeer(info.id);
      this.emit("peers", this.listPeers());
    });
    connection.addEventListener("datachannel", (event) => this.attachChannel(peer, event.channel));

    if (initiator) {
      this.attachChannel(
        peer,
        connection.createDataChannel("nexus-inference", { ordered: true }),
      );
      const offer = await connection.createOffer();
      await connection.setLocalDescription(offer);
      if (!offer.sdp) throw new Error("WebRTC offerにSDPがありません。");
      this.sendSignal({ type: "signal", to: info.id, data: { kind: "offer", sdp: offer.sdp } });
    }
    return peer;
  }

  private async handlePeerSignal(peerId: string, signal: SignalData): Promise<void> {
    try {
      let peer = this.peers.get(peerId);
      if (!peer) {
        const placeholder: SwarmNode = {
          id: peerId,
          name: "接続端末",
          modelId: "",
          lastSeen: Date.now(),
          capabilities: {},
        };
        peer = await this.ensurePeer(placeholder, false);
      }
      if (signal.kind === "offer" || signal.kind === "answer") {
        await peer.connection.setRemoteDescription({ type: signal.kind, sdp: signal.sdp });
        const pendingCandidates = this.takePendingCandidates(peer);
        if (pendingCandidates === null) return;
        for (const candidate of pendingCandidates) {
          await peer.connection.addIceCandidate(candidate);
        }
        if (signal.kind === "offer") {
          const answer = await peer.connection.createAnswer();
          await peer.connection.setLocalDescription(answer);
          if (!answer.sdp) throw new Error("WebRTC answerにSDPがありません。");
          this.sendSignal({ type: "signal", to: peerId, data: { kind: "answer", sdp: answer.sdp } });
        }
      } else if (signal.kind === "ice-complete") {
        if (peer.connection.remoteDescription) await peer.connection.addIceCandidate(null);
      } else if (signal.kind === "ice") {
        const candidate: RTCIceCandidateInit = {
          candidate: signal.candidate,
          sdpMid: signal.sdpMid,
          sdpMLineIndex: signal.sdpMLineIndex,
          usernameFragment: signal.usernameFragment,
        };
        if (peer.connection.remoteDescription) {
          await peer.connection.addIceCandidate(candidate);
        } else {
          this.queuePendingCandidate(peer, candidate);
        }
      }
    } catch {
      // A malformed or unsupported SDP/ICE exchange is scoped to its peer. It
      // must not reject handleSignalMessage and tear down the room WebSocket.
      this.removePeer(peerId);
    }
  }

  private totalPendingCandidateBytes(): number {
    let total = 0;
    for (const peer of this.peers.values()) total += peer.pendingCandidateBytes;
    return total;
  }

  private queuePendingCandidate(peer: PeerRecord, candidate: RTCIceCandidateInit): void {
    const now = Date.now();
    const oldest = peer.pendingCandidates[0];
    if (oldest && now - oldest.queuedAt >= MAX_PENDING_ICE_AGE_MS) {
      this.removePeer(peer.info.id);
      return;
    }

    const byteSize = jsonLength(candidate);
    if (
      peer.pendingCandidates.length >= MAX_PENDING_ICE_CANDIDATES_PER_PEER
      || peer.pendingCandidateBytes + byteSize > MAX_PENDING_ICE_BYTES_PER_PEER
      || this.totalPendingCandidateBytes() + byteSize > MAX_PENDING_ICE_BYTES_TOTAL
    ) {
      this.removePeer(peer.info.id);
      return;
    }

    peer.pendingCandidates.push({ candidate, byteSize, queuedAt: now });
    peer.pendingCandidateBytes += byteSize;
    this.schedulePendingCandidateExpiry(peer);
  }

  private schedulePendingCandidateExpiry(peer: PeerRecord): void {
    if (peer.pendingCandidateExpiryTimer !== null || peer.pendingCandidates.length === 0) return;
    const delay = Math.max(
      1,
      peer.pendingCandidates[0].queuedAt + MAX_PENDING_ICE_AGE_MS - Date.now(),
    );
    peer.pendingCandidateExpiryTimer = globalThis.setTimeout(() => {
      peer.pendingCandidateExpiryTimer = null;
      if (this.peers.get(peer.info.id) !== peer || peer.pendingCandidates.length === 0) return;
      if (Date.now() - peer.pendingCandidates[0].queuedAt >= MAX_PENDING_ICE_AGE_MS) {
        this.removePeer(peer.info.id);
        return;
      }
      this.schedulePendingCandidateExpiry(peer);
    }, delay);
  }

  private takePendingCandidates(peer: PeerRecord): RTCIceCandidateInit[] | null {
    const now = Date.now();
    if (peer.pendingCandidates.some((candidate) => now - candidate.queuedAt >= MAX_PENDING_ICE_AGE_MS)) {
      this.removePeer(peer.info.id);
      return null;
    }
    if (peer.pendingCandidateExpiryTimer !== null) {
      globalThis.clearTimeout(peer.pendingCandidateExpiryTimer);
      peer.pendingCandidateExpiryTimer = null;
    }
    const candidates = peer.pendingCandidates.splice(0).map((entry) => entry.candidate);
    peer.pendingCandidateBytes = 0;
    return candidates;
  }

  private attachChannel(peer: PeerRecord, channel: RTCDataChannel): void {
    peer.channel = channel;
    channel.binaryType = "arraybuffer";
    channel.bufferedAmountLowThreshold = 256_000;
    channel.addEventListener("open", () => {
      this.emit("peers", this.listPeers());
      const nonce = createId("p2p");
      void this.sendPeerMessage(peer.info.id, { kind: "ping", nonce, sentAt: performance.now() });
    });
    channel.addEventListener("close", () => this.emit("peers", this.listPeers()));
    channel.addEventListener("message", (event) => {
      if (typeof event.data !== "string" || event.data.length > CHUNK_CHARACTERS + 1_000) return;
      const now = Date.now();
      if (now - peer.receiveWindowStartedAt >= DATA_RECEIVE_WINDOW_MS) {
        peer.receiveWindowStartedAt = now;
        peer.receivedCharactersInWindow = 0;
      }
      peer.receivedCharactersInWindow += event.data.length;
      if (
        peer.receivedCharactersInWindow > MAX_RECEIVED_CHARACTERS_PER_WINDOW
        || peer.pendingDataMessages >= MAX_QUEUED_DATA_MESSAGES_PER_PEER
      ) {
        this.removePeer(peer.info.id);
        return;
      }
      peer.pendingDataMessages += 1;
      peer.receiveQueue = peer.receiveQueue
        .then(() => this.handleDataMessage(peer.info.id, event.data))
        .catch(() => undefined)
        .finally(() => {
          peer.pendingDataMessages = Math.max(0, peer.pendingDataMessages - 1);
        });
    });
  }

  private async waitForBuffer(channel: RTCDataChannel): Promise<void> {
    if (channel.bufferedAmount < 512_000) return;
    await new Promise<void>((resolve, reject) => {
      const onLow = (): void => {
        window.clearTimeout(timeout);
        resolve();
      };
      const timeout = window.setTimeout(() => {
        channel.removeEventListener("bufferedamountlow", onLow);
        reject(new Error("P2P送信がタイムアウトしました。"));
      }, 8_000);
      channel.addEventListener("bufferedamountlow", onLow, { once: true });
    });
  }

  private async sendPeerMessage(peerId: string, message: PeerMessage): Promise<void> {
    const session = this.requireSession();
    const peer = this.peers.get(peerId);
    const channel = peer?.channel;
    if (!channel || channel.readyState !== "open") throw new Error("P2P DataChannelが未接続です。");
    const encrypted = await encryptJson(session.encryptionKey, session.code, message);
    const serialized = JSON.stringify(encrypted);
    const total = Math.ceil(serialized.length / CHUNK_CHARACTERS);
    if (total > MAX_CHUNKS) throw new Error("分散データが上限を超えています。コンテキストを短くしてください。");
    const messageId = createId("chunk");
    for (let index = 0; index < total; index += 1) {
      await this.waitForBuffer(channel);
      const frame: ChunkFrame = {
        v: 1,
        type: "chunk",
        messageId,
        index,
        total,
        data: serialized.slice(index * CHUNK_CHARACTERS, (index + 1) * CHUNK_CHARACTERS),
      };
      channel.send(JSON.stringify(frame));
    }
  }

  private async handleDataMessage(peerId: string, raw: unknown): Promise<void> {
    if (!this.peers.has(peerId)) return;
    if (typeof raw !== "string" || raw.length > CHUNK_CHARACTERS + 1_000) return;
    let value: unknown;
    try {
      value = JSON.parse(raw);
    } catch {
      return;
    }
    if (!isChunkFrame(value)) return;
    const assemblyKey = `${peerId}:${value.messageId}`;
    const peerAssemblyPrefix = `${peerId}:`;
    if (
      !this.assemblies.has(assemblyKey)
      && [...this.assemblies.keys()].filter((key) => key.startsWith(peerAssemblyPrefix)).length
        >= MAX_ASSEMBLIES_PER_PEER
    ) return;
    if (!this.assemblies.has(assemblyKey) && this.assemblies.size >= MAX_ASSEMBLIES) {
      this.pruneAssemblies(true);
      if (this.assemblies.size >= MAX_ASSEMBLIES) return;
    }
    const assembly = this.assemblies.get(assemblyKey) ?? {
      chunks: Array.from<string>({ length: value.total }).fill(""),
      received: 0,
      receivedCharacters: 0,
      createdAt: Date.now(),
    };
    if (assembly.chunks.length !== value.total) return;
    if (!assembly.chunks[value.index]) {
      this.pruneAssemblies();
      const bufferedCharacters = [...this.assemblies.values()]
        .reduce((total, current) => total + current.receivedCharacters, 0);
      if (bufferedCharacters + value.data.length > MAX_BUFFERED_ASSEMBLY_CHARACTERS) return;
      assembly.chunks[value.index] = value.data;
      assembly.received += 1;
      assembly.receivedCharacters += value.data.length;
    }
    this.assemblies.set(assemblyKey, assembly);
    this.pruneAssemblies();
    if (assembly.received !== value.total) return;
    this.assemblies.delete(assemblyKey);

    let envelope: unknown;
    try {
      envelope = JSON.parse(assembly.chunks.join(""));
    } catch {
      return;
    }
    if (!isRecord(envelope) || typeof envelope.ciphertext !== "string" || typeof envelope.iv !== "string") return;
    const session = this.requireSession();
    let message: PeerMessage;
    try {
      const cipherEnvelope: CipherEnvelope = {
        ciphertext: envelope.ciphertext,
        iv: envelope.iv,
      };
      message = await decryptJson<PeerMessage>(session.encryptionKey, session.code, cipherEnvelope);
    } catch {
      return;
    }
    if (!isPeerMessage(message)) return;
    await this.handlePeerMessage(peerId, message);
  }

  private pruneAssemblies(removeOldest = false): void {
    const cutoff = Date.now() - 30_000;
    for (const [key, value] of this.assemblies) {
      if (value.createdAt < cutoff) this.assemblies.delete(key);
    }
    if (removeOldest && this.assemblies.size >= MAX_ASSEMBLIES) {
      const oldest = [...this.assemblies.entries()]
        .sort((left, right) => left[1].createdAt - right[1].createdAt)
        .slice(0, Math.max(1, this.assemblies.size - MAX_ASSEMBLIES + 1));
      for (const [key] of oldest) this.assemblies.delete(key);
    }
  }

  private async handlePeerMessage(peerId: string, message: PeerMessage): Promise<void> {
    if (message.kind === "ping") {
      await this.sendPeerMessage(peerId, { kind: "pong", nonce: message.nonce, sentAt: message.sentAt });
      return;
    }
    if (message.kind === "pong") {
      const peer = this.peers.get(peerId);
      if (peer) peer.rttMs = Math.max(0, performance.now() - message.sentAt);
      this.emit("latency", { signalingRttMs: this.signalingRttMs, peers: this.listPeers() });
      this.emit("peers", this.listPeers());
      return;
    }
    if (message.kind === "cancel") {
      this.emit<RemoteInferenceCancel>("cancel", { ...message, fromPeerId: peerId });
      return;
    }
    if (message.kind === "layer-ack") {
      const key = this.layerAckKey(peerId, message.ack);
      const pending = this.pendingLayerAcks.get(key);
      if (pending) {
        window.clearTimeout(pending.timeout);
        this.pendingLayerAcks.delete(key);
        pending.resolve();
      }
      return;
    }
    if (message.kind === "layer-manifest") {
      this.emit<RemoteLayerStreamManifest>("layer-manifest", { fromPeerId: peerId, manifest: message.manifest });
      this.appendLedger("layer-manifest-received", message.manifest.modelId, {
        technologyId: message.manifest.technologyId,
        fromPeerId: peerId,
        chunkCount: message.manifest.chunks.length,
        totalLayers: message.manifest.totalLayers,
        fullModelDistributed: message.manifest.fullModelDistributed,
      });
      return;
    }
    if (message.kind === "layer-bytes") {
      this.emit<RemoteLayerBytePacket>("layer-bytes", { fromPeerId: peerId, packet: message.packet });
      await this.sendPeerMessage(peerId, {
        kind: "layer-ack",
        ack: {
          packetKind: "layer-bytes",
          streamId: message.packet.streamId,
          sequence: message.packet.sequence,
          totalSequences: message.packet.totalSequences,
        },
      }).catch(() => undefined);
      return;
    }
    if (message.kind === "activation-packet") {
      this.emit<RemoteActivationPacket>("activation-packet", { fromPeerId: peerId, packet: message.packet });
      await this.sendPeerMessage(peerId, {
        kind: "layer-ack",
        ack: {
          packetKind: "activation-packet",
          streamId: message.packet.streamId,
          sequence: message.packet.sequence,
          totalSequences: message.packet.totalSequences,
        },
      }).catch(() => undefined);
      return;
    }
    if (message.kind === "task") {
      if (!this.acceptTasks || message.payload.modelId !== this.currentModelId) return;
      const taskKey = `${peerId}:${message.taskId}`;
      if (this.seenRemoteTasks.has(taskKey)) return;
      if (!this.admitRemoteTask(peerId, message.payload.maxNewTokens)) return;
      this.seenRemoteTasks.add(taskKey);
      if (this.seenRemoteTasks.size > 512) {
        const oldest = this.seenRemoteTasks.values().next().value;
        if (typeof oldest === "string") this.seenRemoteTasks.delete(oldest);
      }
      this.acceptTasks = false;
      this.sendPresence();
      this.emit<RemoteInferenceTask>("task", { ...message, fromPeerId: peerId });
      return;
    }
    const trustedDeviceName = this.peers.get(peerId)?.info.name ?? "接続端末";
    const result: DistributedInferenceResult = {
      ...message,
      deviceName: trustedDeviceName,
      fromPeerId: peerId,
    };
    const pending = this.pendingBatches.get(result.batchId);
    if (pending?.expectedPeerByTask.get(result.taskId) === peerId) {
      pending.results.set(result.taskId, result);
      if (pending.results.size >= pending.expectedPeerByTask.size) {
        this.settleBatch(result.batchId, [...pending.results.values()]);
      }
    }
    this.emit<DistributedInferenceResult>("result", result);
  }

  private admitRemoteTask(peerId: string, requestedTokens: number): boolean {
    const now = Date.now();
    for (const [id, budget] of this.remoteTaskBudgets) {
      budget.charges = budget.charges.filter(
        (charge) => now - charge.acceptedAt < REMOTE_TASK_WINDOW_MS,
      );
      if (
        budget.charges.length === 0
        && budget.cooldownUntil <= now
        && now - budget.lastSeenAt >= REMOTE_TASK_WINDOW_MS
      ) {
        this.remoteTaskBudgets.delete(id);
      }
    }

    const budget = this.remoteTaskBudgets.get(peerId) ?? {
      charges: [],
      cooldownUntil: 0,
      lastSeenAt: now,
    };
    budget.lastSeenAt = now;
    this.remoteTaskBudgets.set(peerId, budget);
    if (budget.cooldownUntil > now) return false;

    const requestedTokenTotal = budget.charges.reduce(
      (total, charge) => total + charge.requestedTokens,
      0,
    );
    if (
      budget.charges.length >= MAX_REMOTE_TASKS_PER_WINDOW
      || requestedTokenTotal + requestedTokens > MAX_REMOTE_REQUESTED_TOKENS_PER_WINDOW
    ) {
      budget.cooldownUntil = now + REMOTE_TASK_COOLDOWN_MS;
      return false;
    }

    budget.charges.push({ acceptedAt: now, requestedTokens });
    return true;
  }

  eligiblePeers(modelId: string, scope: SwarmScope = "friend"): ConnectedPeer[] {
    return this.listPeers().filter(
      (peer) => peer.modelId === modelId && peer.dataChannelState === "open",
    ).filter(
      (peer) => peer.capabilities.acceptTasks === true,
    ).filter(
      (peer) => scope !== "world" || (
        peer.capabilities.scope === "world"
        && peer.capabilities.worldDistributed === true
      ),
    );
  }

  async requestTasks(
    payload: SwarmGenerationPayload,
    targetTasks: number,
    timeoutMs: number,
    signal?: AbortSignal,
  ): Promise<DistributedInferenceResult[]> {
    const peers = this.eligiblePeers(payload.modelId, this.scope)
      .sort((left, right) => (left.rttMs ?? Number.MAX_SAFE_INTEGER) - (right.rttMs ?? Number.MAX_SAFE_INTEGER))
      .slice(0, Math.max(1, Math.min(4, targetTasks)));
    if (peers.length === 0) throw new Error("同じモデルで準備完了したP2P端末がありません。");
    const roles = ["primary", "critic", "verifier", "alternative"];
    const batchId = createId("batch");
    const tasks = peers.map((peer, index) => ({
      peerId: peer.id,
      taskId: createId("task"),
      role: roles[index] ?? "alternative",
    }));
    const resultPromise = new Promise<DistributedInferenceResult[]>((resolve, reject) => {
      const abortHandler = (): void => {
        void this.cancelBatch(batchId, "生成を停止しました。");
      };
      const timeout = window.setTimeout(() => {
        const pending = this.pendingBatches.get(batchId);
        if (!pending) return;
        const results = [...pending.results.values()];
        if (results.length > 0) this.settleBatch(batchId, results);
        else this.rejectBatch(batchId, new Error("P2P端末からの回答がタイムアウトしました。"));
        void this.sendBatchCancellation(batchId, pending.expectedPeerByTask, pending.results);
      }, timeoutMs);
      this.pendingBatches.set(batchId, {
        expectedPeerByTask: new Map(tasks.map((task) => [task.taskId, task.peerId])),
        results: new Map(),
        resolve,
        reject,
        timeout,
        abortSignal: signal,
        abortHandler,
      });
      signal?.addEventListener("abort", abortHandler, { once: true });
    });

    if (signal?.aborted) {
      await this.cancelBatch(batchId, "生成を停止しました。");
      return resultPromise;
    }

    try {
      await Promise.all(
        tasks.map((task) => this.sendPeerMessage(task.peerId, {
          kind: "task",
          taskId: task.taskId,
          batchId,
          role: task.role,
          payload,
        })),
      );
      this.appendLedger("batch-dispatched", batchId, {
        peers: tasks.map((task) => task.peerId),
        taskIds: tasks.map((task) => task.taskId),
      });
    } catch (error) {
      const failure = error instanceof Error ? error : new Error(String(error));
      this.rejectBatch(batchId, failure);
      await this.sendBatchCancellation(
        batchId,
        new Map(tasks.map((task) => [task.taskId, task.peerId])),
        new Map(),
      );
    }
    return resultPromise;
  }

  async sendLayerStreamManifest(peerId: string, manifest: LayerStreamManifest): Promise<void> {
    if (!isLayerStreamManifest(manifest)) throw new Error("LayerStream manifest is invalid");
    await this.sendPeerMessage(peerId, { kind: "layer-manifest", manifest });
    this.appendLedger("layer-manifest-sent", manifest.modelId, {
      technologyId: manifest.technologyId,
      peerId,
      chunkCount: manifest.chunks.length,
      totalLayers: manifest.totalLayers,
      fullModelDistributed: manifest.fullModelDistributed,
    });
  }

  async sendLayerBytePacket(peerId: string, packet: LayerBytePacket): Promise<void> {
    if (!isLayerBytePacket(packet)) throw new Error("Layer byte packet is invalid");
    await this.sendPeerMessage(peerId, { kind: "layer-bytes", packet });
  }

  async sendActivationPacket(peerId: string, packet: SerializedActivationPacket): Promise<void> {
    if (!isSerializedActivationPacket(packet)) throw new Error("Activation packet is invalid");
    await this.sendPeerMessage(peerId, { kind: "activation-packet", packet });
  }

  async sendLayerBytePacketReliable(peerId: string, packet: LayerBytePacket, attempts = 3): Promise<void> {
    if (!isLayerBytePacket(packet)) throw new Error("Layer byte packet is invalid");
    await this.sendWithLayerAck(
      peerId,
      {
        packetKind: "layer-bytes",
        streamId: packet.streamId,
        sequence: packet.sequence,
        totalSequences: packet.totalSequences,
      },
      { kind: "layer-bytes", packet },
      attempts,
    );
  }

  async sendActivationPacketReliable(peerId: string, packet: SerializedActivationPacket, attempts = 3): Promise<void> {
    if (!isSerializedActivationPacket(packet)) throw new Error("Activation packet is invalid");
    await this.sendWithLayerAck(
      peerId,
      {
        packetKind: "activation-packet",
        streamId: packet.streamId,
        sequence: packet.sequence,
        totalSequences: packet.totalSequences,
      },
      { kind: "activation-packet", packet },
      attempts,
    );
  }

  private layerAckKey(peerId: string, ack: LayerPacketAck): string {
    return `${peerId}:${ack.packetKind}:${ack.streamId}:${ack.sequence}/${ack.totalSequences}`;
  }

  private waitForLayerAck(peerId: string, ack: LayerPacketAck, timeoutMs: number): Promise<void> {
    const key = this.layerAckKey(peerId, ack);
    const previous = this.pendingLayerAcks.get(key);
    if (previous) {
      window.clearTimeout(previous.timeout);
      this.pendingLayerAcks.delete(key);
      previous.reject(new Error("Layer packet ACK wait was replaced"));
    }
    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        this.pendingLayerAcks.delete(key);
        reject(new Error("Layer packet ACK timeout"));
      }, timeoutMs);
      this.pendingLayerAcks.set(key, { resolve, reject, timeout });
    });
  }

  private async sendWithLayerAck(
    peerId: string,
    ack: LayerPacketAck,
    message: Extract<PeerMessage, { kind: "layer-bytes" | "activation-packet" }>,
    attempts: number,
  ): Promise<void> {
    const maxAttempts = Math.max(1, Math.min(5, Math.floor(attempts)));
    let lastError: Error | null = null;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const wait = this.waitForLayerAck(peerId, ack, 4_000 + attempt * 1_500);
      try {
        await this.sendPeerMessage(peerId, message);
        await wait;
        return;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
      }
    }
    throw lastError ?? new Error("Layer packet delivery failed");
  }

  private settleBatch(batchId: string, results: DistributedInferenceResult[]): void {
    const pending = this.pendingBatches.get(batchId);
    if (!pending) return;
    window.clearTimeout(pending.timeout);
    pending.abortSignal?.removeEventListener("abort", pending.abortHandler ?? (() => undefined));
    this.pendingBatches.delete(batchId);
    pending.resolve(results);
  }

  private rejectBatch(batchId: string, error: Error): void {
    const pending = this.pendingBatches.get(batchId);
    if (!pending) return;
    window.clearTimeout(pending.timeout);
    if (pending.abortHandler) pending.abortSignal?.removeEventListener("abort", pending.abortHandler);
    this.pendingBatches.delete(batchId);
    pending.reject(error);
  }

  private async cancelBatch(batchId: string, message: string): Promise<void> {
    const pending = this.pendingBatches.get(batchId);
    if (!pending) return;
    this.rejectBatch(batchId, Object.assign(new Error(message), { name: "AbortError" }));
    await this.sendBatchCancellation(batchId, pending.expectedPeerByTask, pending.results);
  }

  private async sendBatchCancellation(
    batchId: string,
    expected: Map<string, string>,
    completed: Map<string, DistributedInferenceResult>,
  ): Promise<void> {
    await Promise.allSettled(
      [...expected.entries()]
        .filter(([taskId]) => !completed.has(taskId))
        .map(([taskId, peerId]) => this.sendPeerMessage(peerId, { kind: "cancel", taskId, batchId })),
    );
  }

  cancelPendingBatches(): void {
    for (const batchId of [...this.pendingBatches.keys()]) {
      void this.cancelBatch(batchId, "生成を停止しました。");
    }
  }

  async sendResult(
    peerId: string,
    result: Omit<DistributedInferenceResult, "fromPeerId" | "deviceName">,
  ): Promise<void> {
    const session = this.requireSession();
    await this.sendPeerMessage(peerId, {
      kind: "result",
      ...result,
      deviceName: session.deviceName,
    });
    this.appendLedger("task-completed", result.taskId, { batchId: result.batchId, role: result.role });
  }

  sendProgress(taskId: string, batchId: string, state: SwarmProgressState): void {
    if (!/^[A-Za-z0-9_-]{1,64}$/u.test(taskId) || !/^[A-Za-z0-9_-]{1,64}$/u.test(batchId)) {
      throw new Error("進捗IDが不正です。");
    }
    this.sendSignal({ type: "progress", taskId, batchId, state });
  }

  async readChatHistory(maxEntries = 500): Promise<ChatMessage[]> {
    const session = this.requireSession();
    const messages = new Map<string, ChatMessage>();
    let after = 0;
    let bookmark: string | null = null;
    let entriesRead = 0;

    while (entriesRead < maxEntries) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 15_000);
      try {
        const headers = new Headers({ Accept: "application/json" });
        if (bookmark) headers.set("X-D1-Bookmark", bookmark);
        const response = await fetch(
          `/api/swarms/${session.code}/ledger?after=${after}&limit=${Math.min(100, maxEntries - entriesRead)}`,
          { credentials: "same-origin", headers, signal: controller.signal },
        );
        const page = await parseApiResponse<LedgerPageResponse>(response);
        if (!Array.isArray(page.entries) || !page.entries.every(isLedgerEntry)) {
          throw new Error("暗号化履歴の形式が不正です。");
        }
        bookmark = response.headers.get("X-D1-Bookmark") ?? bookmark;
        for (const entry of page.entries) {
          if (entry.eventType !== "chat-turn") continue;
          try {
            const origin = remoteLedgerOrigin(entry);
            // Group-key authentication proves room membership, not authorship.
            // Never render an unattributed shared chat turn as the local user.
            if (!origin) continue;
            const decoded = await decryptJson<unknown>(session.encryptionKey, session.code, {
              ciphertext: entry.ciphertext,
              iv: entry.iv,
            });
            for (const message of parseSyncedChatTurn(decoded)) {
              messages.set(
                message.id,
                origin.nodeId === session.nodeId ? message : { ...message, origin },
              );
            }
          } catch {
            // A corrupt or foreign entry cannot prevent the remaining room history from loading.
          }
        }
        entriesRead += page.entries.length;
        if (page.entries.length < 100 || page.nextAfter <= after) break;
        after = page.nextAfter;
      } finally {
        window.clearTimeout(timeout);
      }
    }
    return [...messages.values()].sort((left, right) => left.createdAt - right.createdAt);
  }

  appendLedger(eventType: string, messageId: string, metadata: Record<string, unknown>): void {
    const session = this.session;
    if (!session) return;
    void encryptJson(session.encryptionKey, session.code, metadata)
      .then((envelope) => {
        if (envelope.ciphertext.length > MAX_LEDGER_CIPHERTEXT_CHARS) return undefined;
        return postLedgerOrQueue({
          swarmCode: session.code,
          eventType,
          messageId,
          ciphertext: envelope.ciphertext,
          iv: envelope.iv,
        });
      })
      .catch(() => undefined);
  }

  private removePeer(peerId: string): void {
    const peer = this.peers.get(peerId);
    if (!peer) return;
    if (peer.pendingCandidateExpiryTimer !== null) {
      globalThis.clearTimeout(peer.pendingCandidateExpiryTimer);
      peer.pendingCandidateExpiryTimer = null;
    }
    try {
      peer.channel?.close();
    } catch {
      // Peer-local WebRTC cleanup must not affect the room signaling socket.
    }
    try {
      peer.connection.close();
    } catch {
      // Peer-local WebRTC cleanup must not affect the room signaling socket.
    }
    this.peers.delete(peerId);
    for (const key of this.assemblies.keys()) {
      if (key.startsWith(`${peerId}:`)) this.assemblies.delete(key);
    }
    for (const [key, pending] of this.pendingLayerAcks) {
      if (key.startsWith(`${peerId}:`)) {
        window.clearTimeout(pending.timeout);
        this.pendingLayerAcks.delete(key);
        pending.reject(new Error("P2P peer disconnected before ACK"));
      }
    }
    this.emit("peers", this.listPeers());
  }

  private requireSession(): SwarmSession {
    if (!this.session) throw new Error("分散グループに接続していません。");
    return this.session;
  }

  private async rollbackSession(deleteOwnedRoom: boolean): Promise<void> {
    const session = this.session;
    if (!session) return;
    this.intentionalClose = true;
    this.stopTimers();
    this.socket?.close(1000, "rollback");
    this.socket = null;
    for (const peerId of [...this.peers.keys()]) this.removePeer(peerId);
    this.remoteTaskBudgets.clear();
    this.session = null;
    const path = deleteOwnedRoom
      ? `/api/swarms/${session.code}`
      : `/api/swarms/${session.code}/leave`;
    await api<unknown>(path, { method: "DELETE" }).catch(() => undefined);
  }

  async leave(): Promise<void> {
    const session = this.session;
    if (!session) return;
    this.intentionalClose = true;
    this.cancelPendingBatches();
    this.stopTimers();
    for (const peerId of [...this.peers.keys()]) this.removePeer(peerId);
    this.remoteTaskBudgets.clear();
    this.socket?.close(1000, "leave");
    this.socket = null;
    this.session = null;
    await api<{ ok: true }>(`/api/swarms/${session.code}/leave`, { method: "DELETE" }).catch(
      () => undefined,
    );
  }
}
